import { trackJourneyEvent } from '@/lib/visitorJourney';
import { queueGTMEvent } from '@/lib/gtm';
import {
  type BillingInterval,
  type CheckoutPlanId,
  getCustomerPlan,
  isCheckoutPlanId,
} from '../../../supabase/functions/_shared/planCatalog.ts';

export const SUBSCRIBE_ITEM_LIST_ID = 'public_plans';
export const SUBSCRIBE_CURRENCY = 'USD';
export const PURCHASE_DEDUP_STORAGE_PREFIX = 'atrivial:ga4:purchase:';

export type SubscribeLeadType = 'enterprise_contact' | 'schedule_demo';

export type SubscribeCheckoutItem = {
  planId: CheckoutPlanId;
  billingInterval: BillingInterval;
};

type EcommerceItem = {
  item_id: string;
  item_name: string;
  item_category: 'subscription';
  item_variant: BillingInterval;
  item_list_id: string;
  price: number;
  quantity: 1;
};

function currentPagePath(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
}

function listPriceUsd(planId: CheckoutPlanId, billingInterval: BillingInterval): number {
  const plan = getCustomerPlan(planId);
  const price = billingInterval === 'year' ? plan.annualPriceUsd : plan.monthlyPriceUsd;
  return price ?? 0;
}

function ecommerceItem(input: SubscribeCheckoutItem): EcommerceItem {
  const plan = getCustomerPlan(input.planId);
  return {
    item_id: input.planId,
    item_name: plan.displayName,
    item_category: 'subscription',
    item_variant: input.billingInterval,
    item_list_id: SUBSCRIBE_ITEM_LIST_ID,
    price: listPriceUsd(input.planId, input.billingInterval),
    quantity: 1,
  };
}

function pushEcommerceEvent(
  event: string,
  ecommerce: Record<string, unknown>,
  extra: Record<string, unknown> = {}
): void {
  queueGTMEvent({ ecommerce: null });
  queueFunnelEvent({
    event,
    ecommerce,
    page_path: currentPagePath(),
    ...extra,
  });
}

export function parseGaClientIdFromCookie(cookie: string): string | null {
  const match = cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.(\d+\.\d+)/);
  return match?.[1] ?? null;
}

export function readGaClientId(): string | null {
  if (typeof document === 'undefined') return null;
  return parseGaClientIdFromCookie(document.cookie);
}

export function rememberPurchaseTransaction(transactionId: string): boolean {
  if (typeof window === 'undefined' || !transactionId) return false;
  const key = `${PURCHASE_DEDUP_STORAGE_PREFIX}${transactionId}`;
  try {
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}

export type NewsletterAction =
  | 'hero_try_trial'
  | 'hero_demo_answer'
  | 'hero_demo_return'
  | 'hero_benefit_habit'
  | 'hero_benefit_sponsorship'
  | 'promo_video_play'
  | 'how_it_works_content'
  | 'how_it_works_brand'
  | 'how_it_works_schedule'
  | 'how_it_works_newsletter'
  | 'how_it_works_readers'
  | 'how_it_works_analytics';

export function trackNewsletterAction(action: NewsletterAction): void {
  if (action === 'hero_demo_answer' || action === 'hero_demo_return') {
    trackJourneyEvent('interaction', action);
  }
  queueFunnelEvent({
    event: 'select_content',
    content_type:
      action === 'hero_try_trial' ? 'cta' : action.startsWith('hero_benefit_') ? 'benefit' : 'demo',
    content_id: action,
    page_path: currentPagePath(),
  });
}

export function trackNavTryTrialCta(): void {
  trackJourneyEvent('signup_click', 'nav_try_trial');
  queueFunnelEvent({
    event: 'select_content',
    content_type: 'cta',
    content_id: 'nav_try_trial',
    page_path: currentPagePath(),
  });
}

export function trackPricingListViewed(billingInterval: BillingInterval): void {
  const items = (['individual', 'business'] as const).map((planId) =>
    ecommerceItem({ planId, billingInterval })
  );
  pushEcommerceEvent(
    'view_item_list',
    {
      item_list_id: SUBSCRIBE_ITEM_LIST_ID,
      item_list_name: 'Public plans',
      items,
    },
    { billing_interval: billingInterval }
  );
}

export function trackBillingIntervalSelected(billingInterval: BillingInterval): void {
  queueFunnelEvent({
    event: 'select_content',
    content_type: 'billing_interval',
    content_id: billingInterval,
    page_path: currentPagePath(),
  });
}

export function trackPlanComparisonOpened(): void {
  queueFunnelEvent({
    event: 'select_content',
    content_type: 'plan_comparison',
    content_id: 'compare_plans_in_detail',
    page_path: currentPagePath(),
  });
}

export function trackBeginCheckout(input: SubscribeCheckoutItem): void {
  trackJourneyEvent('signup_click', input.planId, input.billingInterval);
  trackJourneyEvent('checkout', input.planId, input.billingInterval);
  const item = ecommerceItem(input);
  pushEcommerceEvent(
    'begin_checkout',
    {
      currency: SUBSCRIBE_CURRENCY,
      value: 0,
      items: [item],
    },
    {
      plan_id: input.planId,
      billing_interval: input.billingInterval,
    }
  );
}

export function trackCheckoutAbandoned(input: SubscribeCheckoutItem): void {
  const item = ecommerceItem(input);
  pushEcommerceEvent(
    'checkout_abandoned',
    {
      currency: SUBSCRIBE_CURRENCY,
      value: 0,
      items: [item],
    },
    {
      plan_id: input.planId,
      billing_interval: input.billingInterval,
    }
  );
}

export function trackSubscribePurchase(
  input: SubscribeCheckoutItem & { transactionId: string }
): boolean {
  if (!rememberPurchaseTransaction(input.transactionId)) return false;
  const item = ecommerceItem(input);
  pushEcommerceEvent(
    'purchase',
    {
      transaction_id: input.transactionId,
      currency: SUBSCRIBE_CURRENCY,
      value: 0,
      items: [item],
    },
    {
      transaction_id: input.transactionId,
      plan_id: input.planId,
      billing_interval: input.billingInterval,
    }
  );
  queueFunnelEvent({
    event: 'trial_start',
    transaction_id: input.transactionId,
    plan_id: input.planId,
    billing_interval: input.billingInterval,
    page_path: currentPagePath(),
  });
  return true;
}

export type NewsletterFaqId =
  | 'email_platforms'
  | 'ongoing_quizzes'
  | 'editing_questions'
  | 'email_answers'
  | 'plan_limits'
  | 'trial_and_cancellation';

export function trackNewsletterFaqOpened(faqId: NewsletterFaqId): void {
  queueFunnelEvent({
    event: 'newsletter_faq_open',
    faq_id: faqId,
    page_path: currentPagePath(),
  });
}

// The endpoint is external input: never forward arbitrary error text or unknown codes.
const CHECKOUT_ANALYTICS_CODES = new Set([
  'timeout',
  'INVALID_REQUEST',
  'AUTHENTICATION_EXPIRED',
  'CHECKOUT_ATTEMPT_CONFLICT',
  'CHECKOUT_ATTEMPT_EXPIRED',
  'CHECKOUT_ATTEMPT_COMPLETED',
  'CHECKOUT_ATTEMPT_ABANDONED',
  'CHECKOUT_ATTEMPT_NOT_FOUND',
  'ALREADY_SUBSCRIBED',
  'CHECKOUT_UNAVAILABLE',
  'METHOD_NOT_ALLOWED',
  'UNKNOWN',
]);

export function trackCheckoutLoadError(input: SubscribeCheckoutItem, code: string): void {
  queueFunnelEvent({
    event: 'checkout_error',
    error_stage: 'load_checkout',
    error_code: CHECKOUT_ANALYTICS_CODES.has(code) ? code : 'UNKNOWN',
    plan_id: input.planId,
    billing_interval: input.billingInterval,
    page_path: currentPagePath(),
  });
}

export function trackGenerateLead(leadType: SubscribeLeadType): void {
  trackJourneyEvent('lead', leadType);
  queueFunnelEvent({
    event: 'generate_lead',
    currency: SUBSCRIBE_CURRENCY,
    value: 0,
    lead_type: leadType,
    page_path: currentPagePath(),
  });
}

export function resolveCheckoutPlanId(value: string | null | undefined): CheckoutPlanId | null {
  return isCheckoutPlanId(value) ? value : null;
}

function queueFunnelEvent(event: Record<string, unknown>): void {
  queueGTMEvent({
    // Explicit nulls prevent GTM's persistent data layer from leaking prior attribution.
    section_name: null,
    visible_time_msec: null,
    faq_id: null,
    error_code: null,
    error_stage: null,
    transaction_id: null,
    experiment_id: null,
    experiment_variant: null,
    ...event,
  });
}

/** Owns one landing-page visit; cleanup stops measurement on navigation/unmount. */
export function observeNewsletterLanding(headline: HTMLElement): () => void {
  let headlineSeen = false;

  const measure = () => {
    if (document.visibilityState === 'hidden') return;
    if (!headlineSeen) {
      const rect = headline.getBoundingClientRect();
      if (rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight) {
        headlineSeen = true;
        queueFunnelEvent({ event: 'newsletter_landing_view', page_path: currentPagePath() });
      }
    }
  };
  window.addEventListener('scroll', measure, { passive: true });
  window.addEventListener('resize', measure);
  document.addEventListener('visibilitychange', measure);
  const observer =
    typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(measure);
  observer?.observe(headline);
  measure();
  return () => {
    observer?.disconnect();
    window.removeEventListener('scroll', measure);
    window.removeEventListener('resize', measure);
    document.removeEventListener('visibilitychange', measure);
  };
}

/**
 * Exposure and sustained engagement are separate observations, owned by one page visit.
 * Section names must be ones the homepage-activity function accepts, or it rejects the
 * visitor's whole journey batch.
 */
export function observeNewsletterSections(page: HTMLElement): () => void {
  if (typeof IntersectionObserver === 'undefined') return () => {};
  const states = new Map<
    HTMLElement,
    {
      exposed: boolean;
      engaged: boolean;
      startedAt: number | null;
      visibleTime: number;
      reportedTime: number;
      timer: ReturnType<typeof setTimeout> | undefined;
    }
  >();
  let stopped = false;
  let measurementStarted = false;
  const startMeasurement = () => {
    if (measurementStarted || document.visibilityState === 'hidden') return;
    measurementStarted = true;
    trackJourneyEvent('measurement', 'homepage_engagement');
    trackJourneyEvent('measurement', 'homepage_content');
  };
  startMeasurement();
  const substantiallyVisible = (section: HTMLElement): boolean => {
    if (document.visibilityState === 'hidden') return false;
    const rect = section.getBoundingClientRect();
    const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    return (
      rect.height > 0 &&
      rect.width > 0 &&
      height * width >=
        (Math.min(rect.height, window.innerHeight) * Math.min(rect.width, window.innerWidth)) / 2
    );
  };
  const measure = () => {
    if (stopped) return;
    const now = Date.now();
    for (const [section, state] of states) {
      const visible = state.exposed && substantiallyVisible(section);
      if (!visible) {
        if (state.startedAt !== null) state.visibleTime += now - state.startedAt;
        state.startedAt = null;
        clearTimeout(state.timer);
        state.timer = undefined;
      } else if (state.startedAt === null) {
        state.startedAt = now;
        if (!state.engaged) {
          state.timer = setTimeout(() => {
            state.timer = undefined;
            if (stopped || state.startedAt === null) return;
            if (!substantiallyVisible(section)) {
              measure();
              return;
            }
            state.engaged = true;
            trackJourneyEvent('section_engaged', section.dataset.newsletterSection ?? '');
            queueFunnelEvent({
              event: 'newsletter_section_engaged',
              section_name: section.dataset.newsletterSection,
              page_path: currentPagePath(),
            });
          }, 1000);
        }
      }
    }
  };
  const reportTime = () => {
    const now = Date.now();
    for (const [section, state] of states) {
      const total = state.visibleTime + (state.startedAt === null ? 0 : now - state.startedAt);
      const delta = total - state.reportedTime;
      if (delta < 1) continue;
      state.reportedTime = total;
      trackJourneyEvent(
        'section_time',
        section.dataset.newsletterSection ?? '',
        undefined,
        Math.min(1800000, Math.round(delta))
      );
      queueFunnelEvent({
        event: 'newsletter_section_time',
        section_name: section.dataset.newsletterSection,
        visible_time_msec: delta,
        page_path: currentPagePath(),
      });
    }
  };
  const observer = new IntersectionObserver((entries) => {
    if (stopped || document.visibilityState === 'hidden') return;
    for (const entry of entries) {
      const section = entry.target as HTMLElement;
      const state = states.get(section);
      const name = section.dataset.newsletterSection;
      if (!entry.isIntersecting || !state || !name || state.exposed) continue;
      state.exposed = true;
      trackJourneyEvent('section', name);
      queueFunnelEvent({
        event: 'newsletter_section_view',
        section_name: name,
        page_path: currentPagePath(),
      });
    }
    measure();
  });
  const visibilityChanged = () => {
    startMeasurement();
    measure();
    observer.disconnect();
    if (document.visibilityState === 'hidden') reportTime();
    else for (const section of states.keys()) observer.observe(section);
  };
  const observeSection = (section: HTMLElement) => {
    if (states.has(section)) return;
    states.set(section, {
      exposed: false,
      engaged: false,
      startedAt: null,
      visibleTime: 0,
      reportedTime: 0,
      timer: undefined,
    });
    observer.observe(section);
  };
  for (const section of page.querySelectorAll<HTMLElement>('[data-newsletter-section]')) {
    observeSection(section);
  }
  // Suspense can insert sections after the page effect has started measurement.
  const additions = new MutationObserver((records) => {
    if (stopped) return;
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches('[data-newsletter-section]')) observeSection(node);
        for (const section of node.querySelectorAll<HTMLElement>('[data-newsletter-section]')) {
          observeSection(section);
        }
      }
    }
    measure();
  });
  additions.observe(page, { childList: true, subtree: true });
  document.addEventListener('visibilitychange', visibilityChanged);
  window.addEventListener('scroll', measure, { passive: true });
  window.addEventListener('resize', measure);
  window.addEventListener('pagehide', reportTime);
  return () => {
    measure();
    reportTime();
    stopped = true;
    additions.disconnect();
    for (const state of states.values()) clearTimeout(state.timer);
    observer.disconnect();
    document.removeEventListener('visibilitychange', visibilityChanged);
    window.removeEventListener('scroll', measure);
    window.removeEventListener('resize', measure);
    window.removeEventListener('pagehide', reportTime);
  };
}
